import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Button,
    Text,
    TextInput,
    Theme,
    Icons
} from '@simplybenefits/sb-ui-library';
import Icon from '@mui/material/Icon';
import * as dates from '@simplybenefits/sb-dates';
import Grid from "@mui/material/Grid";
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { getChangeRequests } from '../../../actions/administration.js';
import { styled } from '@mui/system';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import { statuses, types } from '../../../constants/administration.js';
import ResendReviewEmailModal from '../../../components/ChangeRequests/ResendReviewEmailModal.js';
import EditChangeRequestField from '../../../components/ChangeRequests/EditChangeRequestField.js';
import ChangeRequestFiltersModal from './ChangeRequestFiltersModal.js';
import PermissionsComponent from '../../../components/PermissionsComponent.js';

const ChangeRequestRow = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 5,
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    height: 60,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgb(0 0 0 / 15%)',
    '&:hover': {
        filter: 'brightness(97%)'
    }
});

/**
 * 
 * @param {*} userID 
 * @returns 
 */
const getDefaultFilters = (userID, initialAssignedTo) => ({
    sortBy: 'Submission Date',
    sortDirection: 'ASC',
    type: 'All',
    submittedBy: 'All',
    status: userID ? 'All' : 'Pending Agent Review',
    assignedTo: initialAssignedTo,
    ...(userID ? { userID } : {})
});

const styles = {
    headerContainer: {
        height: 30,
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 5,
        justifyContent: 'space-between'
    },
    bodyTitle: {
        backgroundColor: Theme.colors.lightBlue
    },
    firstBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '25%',
        padding: '0 20px'
    },
    centerBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '12%',
        justifyContent: 'center'
    },
    container: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between'
    },
    lastBlock: {
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '20%',
        padding: '0 20px'
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '5%',
        color: Theme.colors.mainTextGrey
    }
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const ChangeRequestsList = ({ isMemberChangeRequestList = false, master, dispatch, loader, accessType, userID = null }) => {
    const { administrators } = master;
    const assignedToMap = Object.fromEntries([
        [ 'Unassigned', null ],
        ...(administrators?.length >= 1 ? administrators.map((e) => [ `${ e.firstname } ${ e.lastname }`, e.masterID ]) : [])
    ]);
    const initialAssignedTo = (!master.isAdministrator || master.access.toUpperCase() === 'MASTER')
        ? 'All'
        : `${ master.firstname } ${ master.lastname }`;

    const [ changesRequests, setChangeRequests ] = useState([]);
    const [ modal, setModal ] = useState(null);
    const [ openedMenu, setOpenedMenu ] = useState(null);
    const [ cursor, setCursor ] = useState(0);
    const [ query, setQuery ] = useState('');
    const [ filters, setFilters ] = useState(() => getDefaultFilters(userID, initialAssignedTo));
    const [ activeFilters, setActiveFilters ] = useState(0);
    const [ changeRequestFiltersModalOpen, setChangeRequestFiltersModalOpen ] = useState(false);

    const modals = {
        reassign: {
            name: 'reassign',
            isConfirmationModal: false,
            currentValue: null
        },
        changeStatus: {
            name: 'changeStatus',
            isConfirmationModal: false,
            currentValue: null
        },
        resendReviewEmail: {
            isConfirmationModal: true
        }
    };

    /**
     *
     * @param nextCursor
     * @returns {Promise<void>}
     * @private
     */
    const _fetchChangeRequests = async (nextCursor) => {
        const result = await dispatch(getChangeRequests({
            ...filters,
            query,
            assignedTo: filters.assignedTo in assignedToMap ? assignedToMap[filters.assignedTo] : 'All'
        }, nextCursor));

        const items = nextCursor > 0
            ? [ ...changesRequests, ...result.items ]
            : result.items;

        setChangeRequests(items);
        setCursor(result.cursor);
    };

    /**
     * @param modalObj
     * @param changeRequestID
     * @param currentValue
     * @private
     */
    const _openModal = (modalObj, changeRequestID, currentValue) => {
        setOpenedMenu(null);
        setModal({ ...modalObj, changeRequestID, currentValue });
    };

    /**
     *
     * @param changeRequestID
     * @private
     */
    const _openChangeRequest = (changeRequestID) => {
        window.open(`/administration/changeRequests/${ changeRequestID }`);
    };

    /**
     * @private
     */
    const _closeModal = () => {
        setModal(null);
    };

    useEffect(() => {

        /**
         * 
         */
        const fetchRequests = async () => {
            await _fetchChangeRequests(0);
    
            if (isMemberChangeRequestList) {
                const defaultFilters = getDefaultFilters(userID, initialAssignedTo);
                const selectedFiltersCount = Object.entries(filters).reduce((count, [ key, value ]) => (
                    defaultFilters[key] !== value ? count + 1 : count
                ), 0);
    
                setActiveFilters(selectedFiltersCount);
            }
        };
    
        fetchRequests();
    }, [ filters ]);

    /**
     * Checks if there are enough change requests to enable cursor-based pagination.
     * 
     * @returns {boolean}
     */
    const hasMoreChangeRequests = () => cursor !== null;

    return (
        <>
            {
                !isMemberChangeRequestList
                    ? ( // show the change request filters above the change request list if we are viewing this component from the administration tab
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            style={{
                                marginBottom: 20
                            }}
                        >
                            <Grid item xs={12} md={6} lg={8} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Search By Change Request Identifier"
                                    fullWidth
                                    value={ query }
                                    variant="outlined"
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyPress={ (e) => {
                                        if (e.key === 'Enter') {
                                            return _fetchChangeRequests(0);
                                        }

                                        return Promise.resolve(false);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={4} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Sort Direction"
                                    fullWidth
                                    value={ filters.sortDirection }
                                    variant="outlined"
                                    onChange={(e) => setFilters({ ...filters, sortDirection: e.target.value })}
                                    select
                                    selectOptions={[
                                        'ASC',
                                        'DESC'
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={6} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Requested Change"
                                    fullWidth
                                    value={ filters.type }
                                    variant="outlined"
                                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                                    select
                                    selectOptions={ [ 'All', ...types ] }
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={6} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Sort By"
                                    fullWidth
                                    value={ filters.sortBy }
                                    variant="outlined"
                                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                                    select
                                    selectOptions={[
                                        'Submission Date',
                                        'Date Signed',
                                        'Status',
                                        'Submitted By'
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={4} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Submitted By"
                                    fullWidth
                                    value={ filters.submittedBy }
                                    variant="outlined"
                                    onChange={(e) => setFilters({ ...filters, submittedBy: e.target.value })}
                                    select
                                    selectOptions={[
                                        'All',
                                        'Administrator',
                                        'Member'
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={4} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Status"
                                    fullWidth
                                    value={ filters.status }
                                    variant="outlined"
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                    select
                                    selectOptions={ [ 'All', ...statuses ] }
                                />
                            </Grid>

                            <Grid item xs={12} md={6} lg={4} style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <TextInput
                                    label="Assigned To"
                                    fullWidth
                                    value={ filters.assignedTo }
                                    variant="outlined"
                                    onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                                    select
                                    selectOptions={ [ 'All', ...Object.keys(assignedToMap) ] }
                                />
                            </Grid>
                        </Grid> 
                    )
                    : ( // show the filter change requests Button if we are viewing this component from the member profile
                        <div style={{ marginTop: -50 }}>
                            {
                                <PermissionsComponent
                                    permission="Administration"
                                    accessType={accessType}
                                    level="read"
                                >
                                    <div style={{ flex: 1, marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            color={ activeFilters > 0 ? 'primary' : 'white' }
                                            noHover
                                            icon="filter-outline"
                                            onClick={ () => setChangeRequestFiltersModalOpen(true) }
                                            id="filterclaims"
                                            customStyle={{
                                                textTransform: "capitalize",
                                                fontSize: 18
                                            }}
                                        >
                                            <Icon
                                                style={{ marginRight: 8, color: 'inherit' }}
                                            >
                                                { Icons.Filter }
                                            </Icon>

                                            {
                                                activeFilters > 0 ?
                                                    `${ activeFilters } ${ activeFilters === 1 ? 'Filter' : 'Filters' } Applied`
                                                    :
                                                    'Filter'
                                            }
                                        </Button>
                                    </div>
                                </PermissionsComponent>
                            }
                            <Text bold customStyle={{ marginBottom: 10 }}>Requested Changes</Text>
                        </div>
                    )
            }

            <div style={ styles.headerContainer }>
                <div style={ styles.firstBlock }>
                    <Text small>Requested Change</Text>
                </div>

                <div style={ styles.centerBlock }>
                    <Text small>Date Signed</Text>
                </div>

                <div style={ styles.centerBlock }>
                    <Text small>Date Submitted</Text>
                </div>

                <div style={ styles.centerBlock }>
                    <Text small>Identifier</Text>
                </div>

                <div style={ styles.centerBlock }>
                    <Text small>Assigned To</Text>
                </div>

                <div style={ styles.lastBlock }>
                    <Text small align="right">Status</Text>
                </div>

                <div style={ styles.iconContainer } />
            </div>

            {
                changesRequests.map((changeRequest) => (
                    <ChangeRequestRow key={ changeRequest.changeRequestID }>
                        <div style={ styles.container } onClick={ () => _openChangeRequest(changeRequest.changeRequestID) } >
                            <div style={{ ...styles.firstBlock, ...styles.bodyTitle }}>
                                <Text small>{ changeRequest.type }</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text small>{ changeRequest.reviewedDate ? dates.formatDateForDisplay(changeRequest.reviewedDate, 'MMM DD, YYYY') : 'N/A' }</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text small>{ dates.formatDateForDisplay(changeRequest.submittedDate, 'MMM DD, YYYY') }</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text small>{ changeRequest.changeRequestID }</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text small>{ changeRequest.assignedTo || 'Unassigned' }</Text>
                            </div>
                            <div style={ styles.lastBlock }>
                                <Text bold small align="right">{ changeRequest.status }</Text>
                            </div>
                        </div>

                        <div style={ styles.iconContainer }>
                            <IconButton
                                id={`menu-button-${ changeRequest.changeRequestID }`}
                                onClick={() => setOpenedMenu(openedMenu === changeRequest.changeRequestID ? null : changeRequest.changeRequestID)}
                            >
                                <MoreVertIcon/>
                            </IconButton>

                            <Menu
                                id={`menu-${ changeRequest.changeRequestID }`}
                                anchorEl={ document.getElementById(`menu-button-${ changeRequest.changeRequestID }`) }
                                open={ openedMenu === changeRequest.changeRequestID }
                                onClose={ () => setOpenedMenu(null)}
                                disableAutoFocusItem={ true }
                                anchorOrigin={ { vertical: "top", horizontal: "left" } }
                            >
                                <PermissionsComponent
                                    permission="Change Request Status Change"
                                    accessType={ accessType }
                                    level="write"
                                >
                                    <MenuItem onClick={ () => _openModal(modals.changeStatus, changeRequest.changeRequestID, changeRequest.status) }>
                                        <Text color="inherit" medium display="inline">
                                        Change Status
                                        </Text>
                                    </MenuItem>
                                </PermissionsComponent>

                                <PermissionsComponent
                                    permission="Change Request Assign Change"
                                    accessType={ accessType }
                                    level="write"
                                >
                                    <MenuItem onClick={ () => _openModal(modals.reassign, changeRequest.changeRequestID, changeRequest.assignedTo) }>
                                        <Text color="inherit" medium display="inline">
                                        Reassign
                                        </Text>
                                    </MenuItem>
                                </PermissionsComponent>

                                <MenuItem onClick={ () => _openModal(modals.resendReviewEmail, changeRequest.changeRequestID, null) }>
                                    <Text color="inherit" medium display="inline">
                                        Resend Review Email
                                    </Text>
                                </MenuItem>
                            </Menu>
                        </div>
                    </ChangeRequestRow>
                ))
            }

            {
                (modal && modal.isConfirmationModal) ?
                    <ResendReviewEmailModal
                        changeRequestID={ modal.changeRequestID }
                        onClose={ _closeModal }
                        loader={ loader }
                        dispatch={ dispatch }
                    />
                    : null
            }

            {
                (modal && !modal.isConfirmationModal) ?
                    <EditChangeRequestField
                        name={ modal.name }
                        currentValue={ modal.currentValue }
                        assignedToMap={ assignedToMap }
                        changeRequestID={ modal.changeRequestID }
                        onClose={ _closeModal }
                        fetchChangeRequests={ _fetchChangeRequests }
                        loader={ loader }
                        dispatch={ dispatch }
                    />
                    : null
            }

            {
                changeRequestFiltersModalOpen ?
                    <ChangeRequestFiltersModal
                        open={changeRequestFiltersModalOpen}
                        close={() => setChangeRequestFiltersModalOpen(false)}
                        filters={filters}
                        setFilters={setFilters}
                        defaultFilters={() => getDefaultFilters(userID, initialAssignedTo)}
                        onClose={ _closeModal }
                        statuses={statuses}
                        assignedToMap={assignedToMap}
                        types={types}
                    />
                    : null
            }

            { isMemberChangeRequestList && changesRequests.length === 0
                ? (
                    <Text>No requested changes yet for this user</Text>
                )
                : (
                    <>
                        {
                            hasMoreChangeRequests()
                                ? (
                                    <Button
                                        onClick={() => _fetchChangeRequests(cursor)}
                                        color="primary"
                                        marginTop={2}
                                        fullWidth
                                    >
                                    Load More
                                    </Button>
                                )
                                : null
                        }
                    </>
                )
            }
        </>
    );
};

/**
 * 
 * @param state
 * @returns {{loader}}
 */
const mapStateToProps = (state) => ({
    master: state?.master,
    loader: state.loader,
    accessType: state.master.access
});

export default connect(mapStateToProps)(ChangeRequestsList);